<template>
  <div>

    <template v-if="mesa !== null">
      <table class="prop-table mb-4">
        <tr>
          <th>Type</th>
          <td>{{mesa.protoId | getMesaTypeName}}</td>
        </tr>
        <tr>
          <th>Player group</th>
          <td>{{mesa.playerGroupId | getPlayerGroupNameVerbose}}</td>
        </tr>
        <tr>
          <th>Client min version</th>
          <td>{{mesa.clientMinVersionCode | convertOptionalVersionCodeToStr('not specified')}}</td>
        </tr>
        <tr>
          <th>
            Status
          </th>
          <td
            :class="getMesaStatusClass(mesa.status)"
          >
            {{mesa.status | getEnumMesaStatusNameFormatted}}
          </td>
        </tr>
        <tr>
          <th>Created at</th>
          <td>{{mesa.ctime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Planned start</th>
          <td>{{mesa.stime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Real start</th>
          <td>{{mesa.srtime | getMesaStartRealTimeStr}}</td>
        </tr>
        <tr>
          <th>Planned duration</th>
          <td>{{plannedDurationStr}}</td>
        </tr>
        <tr>
          <th>Join available until</th>
          <td>{{ mesa.ftime - mesa.prohibitionInterval | getMomentMinStr }}</td>
        </tr>
        <tr>
          <th>Remaining time</th>
          <td>{{getRemainingTimeStr}}</td>
        </tr>
        <tr>
          <th>Planned finish</th>
          <td>{{mesa.ftime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Real finish</th>
          <td>{{mesa.frtime | getMesaFinishRealTimeStr}}</td>
        </tr>
        <tr>
          <th>Real duration</th>
          <td>{{getMesaRealDurationStrSimple(mesa)}}</td>
        </tr>
      </table>

      <b-button
        v-if="canEdit"
        :to="{name: 'mesa_edit', params: {mesaId: this.mesaId}}"
        variant="primary"
      >
        Edit
      </b-button>

      <b-button
        v-if="mustShowForceStartButton"
        @click="forceStartMesaWithConfirmation()"
      >
        Force start
      </b-button> 
    </template>

    <p v-if="message !== null">
      {{message}}
    </p>

    <loading-indicator
      v-if="isLoading"
    />

  </div>
</template>

<script>
/**
 * @typedef { import("../auth.js").Actor } Actor
 */
import auth from '../auth.js';

import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';
import LoadingIndicator from './LoadingIndicator.vue';

import NowSecMixin from '../NowSecMixin.js';

import { getPlayerGroupNameVerbose } from '../model/event.js';

/**
 * @typedef { import("../model/mesa.js").Mesa } Mesa
 */

import { MESA_TYPE_FOSTER_KID_REPEATED              } from '../model/mesa.js';
import { MESA_STATUS_INACTIVE                       } from '../model/mesa.js';
import { MESA_STATUS_ACTIVE                         } from '../model/mesa.js';
import { getMesaTypeName                            } from '../model/mesa.js';
import { getEnumMesaStatusNameFormatted             } from '../model/mesa.js';
import { getMesaStatusClass                         } from '../model/mesa.js';
import { getMesaPlannedDurationStr                  } from '../model/mesa.js';
import { getMesaRemainingTimeStr                    } from '../model/mesa.js';
import { getMesaStartRealTimeStr                    } from '../model/mesa.js';
import { getMesaFinishRealTimeStr                   } from '../model/mesa.js';
import { getMesaRealDurationStrSimple               } from '../model/mesa.js';
import { isMesaEditable                             } from '../model/mesa.js';
import { mayManageMesas as utilMayManageMesas       } from '../model/mesa.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'MesaInfo',

  components: {
    LoadingIndicator,
  },

  mixins: [
    NowSecMixin,
  ],

  props: {
    mesaId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      MESA_TYPE_FOSTER_KID_REPEATED,

      /** @type {?Actor} */
      actor: auth.getViewer(),

      loadState: LOAD_STATE_INIT,

      /** @type {?Mesa} */
      mesa: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && this.mesa === null) {
        return `Mesa with id ${this.mesaId} does not exist.`;
      }
      return null;
    },

    /** @returns {?string} */
    getRemainingTimeStr() {
      const mesa = this.mesa;
      if(mesa === null) {
        return null;
      }

      return getMesaRemainingTimeStr(mesa, this.nowSec);
    },

    /** @returns {?string} */
    plannedDurationStr() {
      const mesa = this.mesa;
      if(mesa === null) {
        return null;
      }

      return getMesaPlannedDurationStr(mesa);
    },

    /** @returns {boolean} */
    canEdit() {
      if(!this.mayManageMesas) {
        return false;
      }

      const mesa = this.mesa;
      if(mesa === null) {
        return false; // data not loaded
      }
      return isMesaEditable(mesa.status);
    },

    /** @returns {boolean} */
    mustShowForceStartButton() {
      if(!this.mayManageMesas) {
        return false;
      }

      const mesa = this.mesa;
      if(mesa === null) {
        return false; // data not loaded
      }

      return mesa.status === MESA_STATUS_INACTIVE;
    },

    /** @returns {boolean} */
    mayManageMesas() {
      const actor = this.actor;
      if(actor === null) {
        return false;
      }

      return utilMayManageMesas(actor.role);
    },
  },

  created() {
    this.loadMesa();
  },

  methods: {
    loadMesa() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = `/mesa/${this.mesaId}/info`;
      HTTP.get(url)
      .then(response => {
        this.mesa = response.data.data;
        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    forceStartMesaWithConfirmation() {
      this.confirmMesaForceStart(() => {
        this.forceStartMesa();
      });
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmMesaForceStart(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to force start this mesa {env}?',
        'Force start',
        onConfirmed,
      );
    },

    forceStartMesa() {
      const mesa = this.mesa;
      if(mesa === null) {
        notifier.showError(`Mesa not loaded.`, {sender: this});
        return;
      }

      notifier.deleteSenderNotifications(this);

      const url = `/mesa/${mesa.id}/force_start`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        mesa.srtime = data.srtime;
        mesa.status = MESA_STATUS_ACTIVE;
        notifier.showSuccess('Mesa started.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    getMesaStatusClass,
    getMesaRealDurationStrSimple,
  },

  filters: {
    getPlayerGroupNameVerbose,
    getMesaTypeName,
    getEnumMesaStatusNameFormatted,
    getMesaStartRealTimeStr,
    getMesaFinishRealTimeStr,
  },
};
</script>
